import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import * as GuardiansV2API from "@/services/api/guardiansv2";
import type { ScreeningDocument } from "@/services/api/guardiansv2/types";
import type { GuardiansFilterValues } from "../shared/GuardiansFilterBar";
import { guardiansErrorMessage } from "../caughtError";
import { useGuardiansReconcile } from "../hooks/useGuardiansReconcile";
import { hasScreeningDisplayContent } from "./screeningPresentation";
import { screeningMatchesSearch } from "./screeningListSearch";

export type ScreeningBucket = "all" | "passed" | "flagged" | "failed";

const BUCKETS: readonly ScreeningBucket[] = ["all", "passed", "flagged", "failed"];

export function isScreeningBucket(value: string): value is ScreeningBucket {
  return (BUCKETS as readonly string[]).includes(value);
}

const PAGE_SIZE = 50;

export interface UseScreeningsOptions {
  filters: GuardiansFilterValues;
  bucket: ScreeningBucket;
  search: string;
  selectedScreeningId: string | null;
  enabled?: boolean;
}

export interface UseScreeningsResult {
  screenings: ScreeningDocument[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  total: number;
  refresh: () => Promise<void>;
  loadMore: () => void;
  hasMore: boolean;
  selectedScreening: ScreeningDocument | null;
  selectedLoading: boolean;
  missingScreeningId: string | null;
}

function matchesBucket(screening: ScreeningDocument, bucket: ScreeningBucket): boolean {
  if (bucket === "all") return true;
  if (bucket === "passed") return !screening.outcome.raised_alert;
  if (bucket === "flagged") return screening.outcome.raised_alert;
  if (bucket === "failed") return screening.checks.failed_count > 0;
  return true;
}

export function useScreenings({
  filters,
  bucket,
  search,
  selectedScreeningId,
  enabled = true,
}: UseScreeningsOptions): UseScreeningsResult {
  const [screenings, setScreenings] = useState<ScreeningDocument[]>([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedScreening, setSelectedScreening] = useState<ScreeningDocument | null>(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [missingScreeningId, setMissingScreeningId] = useState<string | null>(null);

  const filtersKey = JSON.stringify({ filters, bucket });

  const fetchList = useCallback(
    async (append: boolean, currentSkip: number) => {
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError(null);
      try {
        const raised_alert =
          bucket === "flagged" ? true : bucket === "passed" ? false : undefined;

        const res = await GuardiansV2API.listScreenings({
          template_id: filters.missionTemplateId || undefined,
          task_id: filters.taskId || undefined,
          agent_id: filters.agentId || undefined,
          catalogue_id: filters.catalogueId || undefined,
          raised_alert,
          time_period: "all",
          skip: currentSkip,
          limit: PAGE_SIZE,
        });

        let rows = res.screenings;
        if (bucket === "failed") {
          rows = rows.filter((s) => s.checks.failed_count > 0);
        }

        setScreenings((prev) => (append ? [...prev, ...rows] : rows));
        setTotal(res.total);
        setSkip(currentSkip + rows.length);
      } catch (err) {
        setError(guardiansErrorMessage(err, "Failed to load screenings"));
      } finally {
        if (append) setLoadingMore(false);
        else setLoading(false);
      }
    },
    [filters, bucket]
  );

  const refresh = useCallback(() => {
    setSkip(0);
    return fetchList(false, 0);
  }, [fetchList]);

  useEffect(() => {
    if (!enabled) {
      setScreenings([]);
      setTotal(0);
      setSkip(0);
      setLoading(false);
      setLoadingMore(false);
      setError(null);
      return;
    }
    setSkip(0);
    void fetchList(false, 0);
  }, [enabled, filtersKey, fetchList]);

  useGuardiansReconcile(refresh, enabled);

  useLayoutEffect(() => {
    if (!selectedScreeningId) {
      setSelectedScreening(null);
      setSelectedLoading(false);
      setMissingScreeningId(null);
      return;
    }
    let mounted = true;
    setSelectedLoading(true);
    setMissingScreeningId(null);
    GuardiansV2API.getScreening(selectedScreeningId, { include_workload: true })
      .then((doc) => {
        if (!mounted) return;
        if (!hasScreeningDisplayContent(doc)) {
          setSelectedScreening(null);
          setMissingScreeningId(selectedScreeningId);
          return;
        }
        setSelectedScreening(doc);
      })
      .catch(() => {
        if (!mounted) return;
        setSelectedScreening(null);
        setMissingScreeningId(selectedScreeningId);
      })
      .finally(() => {
        if (mounted) setSelectedLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [selectedScreeningId]);

  const visible = useMemo(
    () =>
      screenings.filter(
        (s) =>
          hasScreeningDisplayContent(s) && matchesBucket(s, bucket) && screeningMatchesSearch(s, search)
      ),
    [screenings, bucket, search]
  );

  const hasMore = skip < total;

  const loadMore = useCallback(() => {
    if (!hasMore || loading || loadingMore) return;
    void fetchList(true, skip);
  }, [hasMore, loading, loadingMore, fetchList, skip]);

  return {
    screenings: visible,
    loading,
    loadingMore,
    error,
    total,
    refresh,
    loadMore,
    hasMore,
    selectedScreening,
    selectedLoading,
    missingScreeningId,
  };
}
